import React from 'react';
import DatePicker from 'react-datepicker';
import { tr } from 'date-fns/locale';
import { Icon } from '../ui';
import { useTranslation } from '../context/TranslationContext';
import 'react-datepicker/dist/react-datepicker.css';
import './datepicker.css';
import styles from './DateRangePicker.module.css';

/**
 * DateRangePicker — modern, token-themed start/end picker.
 *
 * Replaces native `<input type="date">` pairs in report filters so the
 * controls render consistently across browsers and dark/light themes.
 *
 * Props:
 *   start, end:                   Date | null
 *   onChange({start, end}):       fires on either side changing; emits both
 *   granularity:                  'day' | 'month'   (default 'day')
 *   minDate, maxDate:             optional bounds
 *   startLabel, endLabel:         optional sr labels (uses i18n by default)
 *   className:                    wrapper class override
 */
const DateRangePicker = ({
  start,
  end,
  onChange,
  granularity = 'day',
  minDate,
  maxDate,
  startLabel,
  endLabel,
  className = '',
}) => {
  const { t, language } = useTranslation();
  const locale = language === 'tr' ? tr : undefined;
  const dateFormat = granularity === 'month' ? 'MMMM yyyy' : 'd MMM yyyy';
  const showMonthYearPicker = granularity === 'month';

  return (
    <div className={`${styles.wrap} ${className}`}>
      <div className={styles.field}>
        <span className={styles.leadingIcon} aria-hidden="true">
          <Icon name="fa-calendar" size={14} />
        </span>
        <DatePicker
          selected={start}
          onChange={(d) => onChange({ start: d, end })}
          selectsStart
          startDate={start}
          endDate={end}
          maxDate={end || maxDate}
          minDate={minDate}
          dateFormat={dateFormat}
          showMonthYearPicker={showMonthYearPicker}
          locale={locale}
          placeholderText={startLabel || t('selectStartDate')}
          className={`${styles.input} fis4j-datepicker-input`}
        />
      </div>

      <span className={styles.divider} aria-hidden="true">
        <Icon name="fa-arrow-right" size="10px" />
      </span>

      <div className={styles.field}>
        <span className={styles.leadingIcon} aria-hidden="true">
          <Icon name="fa-calendar" size={14} />
        </span>
        <DatePicker
          selected={end}
          onChange={(d) => onChange({ start, end: d })}
          selectsEnd
          startDate={start}
          endDate={end}
          minDate={start || minDate}
          maxDate={maxDate}
          dateFormat={dateFormat}
          showMonthYearPicker={showMonthYearPicker}
          locale={locale}
          placeholderText={endLabel || t('selectEndDate')}
          className={`${styles.input} fis4j-datepicker-input`}
        />
      </div>
    </div>
  );
};

export default DateRangePicker;
